import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { BoggleGameModeDefinition, BoggleGameModeId } from '~/boggle/gameModes'
import { evaluateRunSummaryScore } from '~/boggle/config/runSummaryScore'
import { evaluateBoggleRunBadges } from '~/boggle/lib/badges/evaluateRunBadges'
import { getBoggleChallengeScoreRank } from '~/boggle/lib/challenge/scoreRank'
import {
  buildRunSummaryAchievementEvents,
  type RunSummaryAchievementEvent,
} from '~/boggle/lib/achievements/runSummaryAchievementEvents'
import {
  createBoggleCompletedRunRecord,
  type BoggleRunAttemptIdentity,
} from '~/boggle/progress/runCompletion'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'
import { ChallengeContextScoreTray } from '~/boggle/ui/challenge/ChallengeContextScoreTray'
import { GamePageHeader } from '~/boggle/ui/chrome/AppLayout'
import { RunSurface } from '~/boggle/ui/run/RunSurface'
import type { BoggleChallengeSummaryOutcome } from '~/boggle/ui/run/RunSummary'
import { RunVariantShell } from '~/boggle/ui/run/RunVariantShell'
import {
  hasActiveBoggleScoreFeedback,
  useBoggleScoreFeedback,
} from '~/boggle/ui/run/ScoreFeedback'
import { useBoggleUiState, type BoggleStore } from '~/boggle/ui/state/Store'
import {
  captureBoggleException,
  captureBoggleRunAbandoned,
  captureBoggleRunCompleted,
} from '~/boggle/telemetry/boggleTelemetry'

export type ChallengeRunMode = 'challenge' | 'practice'

export type ChallengeRunSummaryAction = {
  label: string
  onClick: () => void
  tone?: 'challenge' | 'cta' | 'neutral' | 'warm'
}

/**
 * Challenge run variant (also handles practice). Owns the Challenge tray,
 * challenge best-score reads, target/ace outcome calculation, and the
 * back-to-staging affordance via pause overlay + tray label.
 *
 * In `'practice'` mode, the run summary is skipped entirely: as soon as score
 * feedback settles after `phase === 'ended'`, the variant auto-navigates back
 * to staging.
 */
export function ChallengeRunSurface({
  accentColor,
  aceScore,
  gameModeId,
  gameRulesDefinition,
  mode,
  onBackToChallengeStaging,
  onNewGame,
  onRestartSeed,
  progressSeed,
  runAttempt,
  store,
  summaryActions,
  targetScore,
  telemetryEnabled,
}: {
  accentColor?: string
  aceScore: number
  gameModeId: BoggleGameModeId
  gameRulesDefinition?: BoggleGameModeDefinition
  mode: ChallengeRunMode
  /** Returns to the challenge staging/reveal screen (used by pause, tray label, summary back). */
  onBackToChallengeStaging: () => void
  onNewGame: () => void
  onRestartSeed?: () => void
  /** Storage key for challenge best-score projection lookup. */
  progressSeed: number | string
  runAttempt: BoggleRunAttemptIdentity
  store: BoggleStore
  summaryActions?: ChallengeRunSummaryAction[]
  targetScore: number
  telemetryEnabled: boolean
}) {
  const state = useBoggleUiState(store)
  const scoreFeedback = useBoggleScoreFeedback(store)
  const handleRestartBase = onRestartSeed ?? onNewGame

  const [bestScore, setBestScore] = useState<null | number>(null)
  const [achievementEvents, setAchievementEvents] = useState<
    readonly RunSummaryAchievementEvent[] | null
  >(null)

  const refreshDisplayedBestScore = useCallback(() => {
    if (mode !== 'challenge') {
      setBestScore(null)
      return
    }

    void getBoggleProgressRepository()
      .getChallengeBestScore(gameModeId, progressSeed)
      .then(setBestScore)
      .catch(() => setBestScore(null))
  }, [gameModeId, mode, progressSeed])

  useEffect(() => {
    refreshDisplayedBestScore()
  }, [refreshDisplayedBestScore])

  const earnedBadges = useMemo(
    () => evaluateBoggleRunBadges(state.runLedger),
    [state.runLedger],
  )
  const summaryScore = useMemo(
    () =>
      evaluateRunSummaryScore({
        earnedBadges,
        goldTilesUsed: state.goldTilesUsed,
        scoredWords: state.scoredWords,
        tilesCleared: state.tilesCleared,
      }),
    [earnedBadges, state.goldTilesUsed, state.scoredWords, state.tilesCleared],
  )
  const runTotal = state.phase === 'ended' ? summaryScore.total : null
  const runKind = mode === 'challenge' ? 'challenge' : 'challenge-practice'
  const abandonedRunSignatureRef = useRef<string | null>(null)
  const completedTelemetrySignatureRef = useRef<string | null>(null)

  const captureAbandonedRun = useCallback(
    (reason: 'exit' | 'restart') => {
      if (!telemetryEnabled || state.phase !== 'playing') {
        return
      }

      const signature = runAttempt.id
      if (abandonedRunSignatureRef.current === signature) {
        return
      }

      abandonedRunSignatureRef.current = signature
      captureBoggleRunAbandoned({
        gameModeId,
        reason,
        runAttempt,
        runKind,
        state,
      })
    },
    [gameModeId, runAttempt, runKind, state, telemetryEnabled],
  )

  useEffect(() => {
    if (state.phase !== 'ended') {
      completedTelemetrySignatureRef.current = null
      if (state.phase === 'playing') {
        abandonedRunSignatureRef.current = null
      }
      return
    }

    if (!telemetryEnabled) {
      return
    }

    if (runKind !== 'challenge-practice') {
      return
    }

    const signature = runAttempt.id
    if (completedTelemetrySignatureRef.current === signature) {
      return
    }

    completedTelemetrySignatureRef.current = signature
    captureBoggleRunCompleted({
      gameModeId,
      runAttempt,
      runKind,
      state,
      summaryScore,
    })
  }, [gameModeId, runAttempt, runKind, state, summaryScore, telemetryEnabled])

  /**
   * Badge progress commits exactly once per run, the moment a qualifying
   * challenge run reaches `'ended'`. Practice runs are explicitly excluded —
   * they don't show results and shouldn't contribute to compendium progress.
   */
  const committedRunSignatureRef = useRef<string | null>(null)
  useEffect(() => {
    if (mode !== 'challenge' || state.phase !== 'ended') {
      committedRunSignatureRef.current = null
      setAchievementEvents(null)
      return
    }
    const signature = runAttempt.id
    if (committedRunSignatureRef.current === signature) {
      return
    }
    committedRunSignatureRef.current = signature
    const completedRun = createBoggleCompletedRunRecord({
      attempt: runAttempt,
      completedAt: new Date().toISOString(),
      earnedBadges,
      gameModeId,
      runLedger: state.runLedger,
      runKind: mode === 'challenge' ? 'challenge' : 'challenge-practice',
      summaryScore,
      submittedWords: state.scoredWords,
    })
    if (completedRun) {
      void getBoggleProgressRepository()
        .commitCompletedRun(completedRun)
        .then(result => {
          if (committedRunSignatureRef.current !== signature) {
            return
          }
          if (
            telemetryEnabled &&
            completedTelemetrySignatureRef.current !== signature
          ) {
            completedTelemetrySignatureRef.current = signature
            captureBoggleRunCompleted({
              gameModeId,
              progressSummary: result.inserted ? result.progressSummary : undefined,
              runAttempt,
              runKind,
              state,
              summaryScore,
            })
          }
          setAchievementEvents(
            result.inserted
              ? buildRunSummaryAchievementEvents({
                  newlyRevealedRatingIds: result.newlyRevealedRatingIds,
                  newlyUnlockedTierIds: result.newlyUnlockedTierIds,
                })
              : [],
          )
        })
        .catch(error => {
          console.error('[boggle] failed to persist completed challenge run', error)
          captureBoggleException(error, {
            gameModeId,
            operation: 'commit_completed_challenge_run',
            runId: runAttempt.id,
            runKind,
          })
          if (committedRunSignatureRef.current === signature) {
            setAchievementEvents([])
          }
        })
    } else {
      setAchievementEvents([])
    }
  }, [
    earnedBadges,
    gameModeId,
    mode,
    runAttempt,
    runKind,
    state.phase,
    state.runLedger,
    state.scoredWords,
    summaryScore,
  ])

  const handleBackToChallengeStaging = useCallback(() => {
    captureAbandonedRun('exit')
    refreshDisplayedBestScore()
    onBackToChallengeStaging()
  }, [captureAbandonedRun, onBackToChallengeStaging, refreshDisplayedBestScore])

  const handleNewGame = useCallback(() => {
    refreshDisplayedBestScore()
    onNewGame()
  }, [onNewGame, refreshDisplayedBestScore])

  const handleRestart = useCallback(() => {
    captureAbandonedRun('restart')
    handleRestartBase()
  }, [captureAbandonedRun, handleRestartBase])

  const challengeOutcome: BoggleChallengeSummaryOutcome | null = useMemo(() => {
    if (mode !== 'challenge' || runTotal === null) {
      return null
    }
    const rank = getBoggleChallengeScoreRank(runTotal, { aceScore, targetScore })
    return rank === 'ace' ? 'ace' : rank === 'target' ? 'success' : 'target-missed'
  }, [aceScore, mode, runTotal, targetScore])

  // Practice runs do not show a summary; auto-exit once score feedback settles.
  // Pinned ref to avoid re-running the effect when navigation callback identity changes.
  const onBackRef = useRef(onBackToChallengeStaging)
  onBackRef.current = onBackToChallengeStaging
  const practiceExitedRef = useRef(false)

  useEffect(() => {
    if (state.phase !== 'ended') {
      practiceExitedRef.current = false
    }
  }, [state.phase])

  useEffect(() => {
    if (mode !== 'practice' || state.phase !== 'ended') {
      return
    }
    if (hasActiveBoggleScoreFeedback(scoreFeedback) || practiceExitedRef.current) {
      return
    }
    practiceExitedRef.current = true
    startTransition(() => {
      onBackRef.current()
    })
  }, [
    mode,
    scoreFeedback.boardPulse?.id,
    scoreFeedback.events.length,
    scoreFeedback.pendingEventCount,
    state.phase,
  ])

  return (
    <RunVariantShell scoreFeedback={scoreFeedback} state={state} store={store}>
      <RunSurface
        accentColor={accentColor}
        gameRulesDefinition={gameRulesDefinition}
        onRestart={handleRestart}
        pauseConfig={{ onBackToChallenge: handleBackToChallengeStaging }}
        scoreFeedback={scoreFeedback}
        state={state}
        store={store}
        summary={{
          achievementEvents: mode === 'challenge' ? achievementEvents : [],
          actions: summaryActions,
          challengeOutcome,
          onBack: handleBackToChallengeStaging,
          onNewGame: handleNewGame,
          summaryScore,
        }}
        topChrome={
          <GamePageHeader
            content={
              <ChallengeContextScoreTray
                aceScore={aceScore}
                bestScore={mode === 'challenge' ? bestScore : null}
                contextLabelClick={() => {
                  startTransition(() => {
                    onBackToChallengeStaging()
                  })
                }}
                mode={mode}
                targetScore={targetScore}
              />
            }
          />
        }
      />
    </RunVariantShell>
  )
}
